import { useState } from "react";
import { PricingCard } from "./PricingCard";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { openPaddleCheckout } from "@/lib/paddle";
import { PLANS, formatUsd, type PlanId, type Plan } from "@/config/pricing";

interface PricingSectionProps {
  businessType?: string | null;
}

export const PricingSection = ({ businessType }: PricingSectionProps) => {
  const [loading, setLoading] = useState<PlanId | null>(null);

  const handleSelectPlan = async (planId: PlanId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.info("Please sign in to subscribe");
        window.location.href = `/auth?redirect=${encodeURIComponent("/pricing")}`;
        return;
      }

      setLoading(planId);
      const plan = PLANS[planId];

      await openPaddleCheckout({
        priceId: plan.priceId,
        email: user.email || "",
        userId: user.id,
        planId: plan.id,
      });
    } catch (error: any) {
      console.error(`Checkout error for ${planId}:`, error);
      toast.error(error.message || "Unable to open checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const planEntries = Object.entries(PLANS) as Array<[PlanId, Plan]>;

  return (
    <section className="py-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold">Choose Your Monthly Plan</h2>
        <p className="text-muted-foreground">
          {businessType
            ? `Plans that scale with your ${businessType} business. Cancel anytime.`
            : "Simple monthly pricing in USD. Cancel anytime."}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 max-w-6xl gap-8 mx-auto px-4">
        {planEntries.map(([key, plan]) => (
          <PricingCard
            key={key}
            title={plan.name}
            description={plan.description}
            price={`${formatUsd(plan.price)}/mo`}
            features={plan.features}
            popular={plan.popular}
            loading={loading === key}
            onSelect={() => handleSelectPlan(key)}
          />
        ))}
      </div>
    </section>
  );
};
